"use client";

import { useEffect, useRef, useState } from "react";
import type {
  FeedbackPayload,
  FeedbackScreenshot,
  FeedbackType,
  FeedbackWidgetProps,
  ScreenshotSource,
} from "./types";
import { sendFeedback, uploadScreenshot } from "./api";
import { injectStyles } from "./styles";
import {
  loadScreenshotLib,
  collectFontFaceCss,
  detectRootBackground,
  cropToViewport,
  normalizeImageToJpeg,
} from "./lazy-screenshot";
import { ScreenshotEditor } from "./ScreenshotEditor";
import { initConsoleBuffer, getConsoleBuffer } from "./console-buffer";
import { WIDGET_VERSION, WIDGET_VERSION_LABEL } from "./version";

/**
 * Copy text to the clipboard, with a fallback for insecure contexts.
 *
 * Why the fallback: the widget runs in the HOST page's security context,
 * and `navigator.clipboard` only exists in a secure context (HTTPS or
 * localhost). A host page opened over plain `http://` on a LAN IP — which
 * is exactly how dev/staging often gets viewed from another device — is
 * NOT a secure context, so `navigator.clipboard` is `undefined` and the
 * Async Clipboard API silently does nothing. The legacy hidden-textarea +
 * `execCommand("copy")` path still works there and on older browsers.
 *
 * Returns whether the copy actually succeeded so the caller only shows
 * "Copied" when it did.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Secure-context API present but refused (focus/permission) — fall
    // through to the legacy path rather than giving up.
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

type ChipMeta = {
  value: FeedbackType;
  label: string;
  glyph: string;
  color: string;
  tint: string;
};

const TYPES: ChipMeta[] = [
  { value: "bug", label: "Bug", glyph: "B", color: "#e5484d", tint: "#e5484d" },
  { value: "idea", label: "Idea", glyph: "I", color: "#bf8b00", tint: "#f0b429" },
  { value: "praise", label: "Praise", glyph: "P", color: "#e93d82", tint: "#e93d82" },
  { value: "question", label: "Question", glyph: "?", color: "#3e63dd", tint: "#3e63dd" },
];

const PLACEHOLDERS: Record<FeedbackType, string> = {
  bug: "What went wrong? Steps to reproduce help.",
  idea: "What would make this better?",
  praise: "What's working? We need this too.",
  question: "Ask away — we read everything.",
};

const MAX_LEN = 10000;
// Per-item screenshot count cap and per-file source-size cap (matches the
// storage bucket's 10 MB limit). Re-encoding to JPEG shrinks most uploads well
// under this, but we reject oversized sources before touching a canvas.
const MAX_SHOTS = 6;
const MAX_SRC_BYTES = 10 * 1024 * 1024;

type Status = "idle" | "sending" | "sent" | "error";

type ShotEntry = {
  id: string;
  blob: Blob;
  dataUrl: string;
  source: ScreenshotSource;
};

export function FeedbackWidget({
  apiKey,
  endpoint,
  user,
  position = "bottom-right",
  theme,
  label,
  hidden = false,
  offset,
}: FeedbackWidgetProps) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>("bug");
  const [body, setBody] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [duration, setDuration] = useState<number | null>(null);
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [provenance, setProvenance] = useState<{ host: string; vp: string } | null>(null);
  const [screenshots, setScreenshots] = useState<ShotEntry[]>([]);
  const [shotStatus, setShotStatus] = useState<"idle" | "capturing" | "editing">("idle");
  const [pendingShot, setPendingShot] = useState<{ blob: Blob; dataUrl: string } | null>(null);
  // null = the pending shot is a fresh capture to append; a number = we're
  // re-annotating the existing entry at that index (its source is preserved).
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [attachNote, setAttachNote] = useState("");
  // Shown while the Paste button is hovered/focused so the keyboard-driven
  // paste flow is discoverable (it only captures images from the clipboard).
  const [showPasteHint, setShowPasteHint] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const shotIdRef = useRef(0);
  const nextShotId = () => `s${++shotIdRef.current}`;

  // First-visit attention pull on the FAB: full opacity + gentle entrance,
  // then settles to the resting low-opacity state. Once-per-browser via
  // localStorage; returning users get the resting state from first paint.
  const [firstVisit, setFirstVisit] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (!window.localStorage.getItem("fh-fab-seen")) {
        setFirstVisit(true);
        window.localStorage.setItem("fh-fab-seen", "1");
      }
    } catch {
      // private mode / disabled storage — silently skip the entrance.
    }
  }, []);

  // Draft autosave. Closing the widget mid-write used to lose the text;
  // now we persist (body + type) to localStorage scoped per (apiKey, path)
  // so a draft is restored when you reopen on the same page. Cleared on
  // successful submit. Drafts older than 7 days are ignored on restore.
  const draftKey = (() => {
    if (typeof window === "undefined") return "";
    const path = window.location.pathname || "/";
    return `fh-draft:${apiKey}:${path}`;
  })();
  const draftHydrated = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!draftKey) return;
    try {
      const raw = window.localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        body?: string;
        type?: FeedbackType;
        savedAt?: number;
      };
      const STALE_MS = 7 * 24 * 60 * 60 * 1000;
      if (parsed.savedAt && Date.now() - parsed.savedAt > STALE_MS) {
        window.localStorage.removeItem(draftKey);
        return;
      }
      if (parsed.body) setBody(parsed.body);
      if (parsed.type && TYPES.some((t) => t.value === parsed.type)) {
        setType(parsed.type);
      }
    } catch {
      // corrupt entry — drop it silently.
      try {
        window.localStorage.removeItem(draftKey);
      } catch {}
    } finally {
      draftHydrated.current = true;
    }
  }, [draftKey]);
  useEffect(() => {
    if (typeof window === "undefined" || !draftKey) return;
    // Skip the first effect run — that's the post-hydrate render that
    // would otherwise immediately overwrite the just-restored value
    // (e.g. clearing body would persist the empty state before the user
    // typed anything).
    if (!draftHydrated.current) return;
    try {
      if (body.trim().length === 0) {
        window.localStorage.removeItem(draftKey);
      } else {
        window.localStorage.setItem(
          draftKey,
          JSON.stringify({ body, type, savedAt: Date.now() }),
        );
      }
    } catch {
      // storage unavailable — drop silently.
    }
  }, [body, type, draftKey]);

  useEffect(() => {
    if (typeof document !== "undefined") injectStyles(document);
    // Wire the console + uncaught-error buffer right at mount so we collect
    // logs that happen before the user clicks the widget.
    initConsoleBuffer();
  }, []);

  // Compute provenance when the panel opens, so the user can see what will be sent.
  useEffect(() => {
    if (!open) return;
    let host = "";
    try {
      const u = new URL(window.location.href);
      const path = u.pathname === "/" ? "" : u.pathname;
      host = `${u.host}${path}`;
    } catch {
      host = window.location.href;
    }
    if (host.length > 42) host = `${host.slice(0, 40)}…`;
    setProvenance({
      host,
      vp: `${window.innerWidth}×${window.innerHeight}`,
    });
  }, [open]);

  // ESC closes + focus trap.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        fabRef.current?.focus();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => textareaRef.current?.focus(), 80);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open]);

  // Reset success state when the panel is RE-OPENED — not whenever
  // status changes. The original dep array `[open, status]` caused the
  // effect to fire the moment `setStatus("sent")` ran post-submit,
  // which immediately reset status back to "idle" before the success
  // state could ever render. The 1.7s auto-close hid the bug; the
  // tracking-link's 6s window exposed it.
  // Now: only fire on `open` transitions, and use a ref to skip the
  // initial mount so we don't reset state that was never "sent" yet.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (!prevOpenRef.current && open && status === "sent") {
      setStatus("idle");
      setDuration(null);
      setViewUrl(null);
      setLinkCopied(false);
    }
    prevOpenRef.current = open;
  }, [open, status]);

  // Auto-grow textarea (capped via CSS max-height).
  useEffect(() => {
    const t = textareaRef.current;
    if (!t) return;
    t.style.height = "auto";
    t.style.height = `${Math.min(t.scrollHeight, 240)}px`;
  }, [body]);

  if (hidden) return null;

  const accent = theme?.accent ?? "#111";
  const positionClass = position === "bottom-left" ? "fh-pos-bl" : "fh-pos-br";
  const charCount = body.length;
  const overLimit = charCount > MAX_LEN;
  const canSubmit =
    status !== "sending" && body.trim().length > 0 && !overLimit;
  const activeChip = TYPES.find((t) => t.value === type) ?? TYPES[0];

  async function handleCaptureScreenshot() {
    if (shotStatus === "capturing") return;
    if (screenshots.length >= MAX_SHOTS) {
      setAttachNote(`You can attach up to ${MAX_SHOTS} images.`);
      return;
    }
    console.log("[feedback-hub] capture: start");
    setShotStatus("capturing");
    setErrorMessage("");
    setAttachNote("");

    // Belt + suspenders: filter the .fh-root subtree via the screenshot lib's
    // own API, AND hide the FAB itself via display:none during capture so
    // there's no chance the mobile edge-pill sneaks into the output. We
    // can't hide the whole .fh-root because the "Drawing the page" capturing
    // overlay lives inside it. The FAB is the only chrome that's actually
    // on-screen during the capture.
    const rootEl = panelRef.current?.closest(".fh-root") as HTMLElement | null;
    const fabEl = rootEl?.querySelector<HTMLElement>(".fh-fab") ?? null;
    const prevDisplay = fabEl?.style.display ?? "";
    if (fabEl) fabEl.style.display = "none";

    // Hard timeout so a wedged screenshot lib can't strand the UI.
    const TIMEOUT_MS = 20_000;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      console.warn("[feedback-hub] capture timed out after 20s");
      if (fabEl) fabEl.style.display = prevDisplay;
      setErrorMessage("Capture took too long. Try again on a smaller area or close some modals?");
      setShotStatus("idle");
    }, TIMEOUT_MS);

    try {
      const lib = await loadScreenshotLib(endpoint);
      console.log("[feedback-hub] capture: modern-screenshot loaded");
      // Capture the <html> element, NOT <body>. modern-screenshot sizes the
      // canvas from the captured node's getBoundingClientRect() and re-lays
      // the clone out inside a foreignObject of that width. document.body's box
      // is NOT the browser viewport — any page that centers/max-widths the
      // body, gives it margins, or anchors elements to the viewport
      // (position:fixed, vw units, full-bleed backgrounds) renders at the wrong
      // width and shifts or drops those elements (reported: a stray "1"
      // appearing off-position). <html> IS the layout viewport, so the capture
      // width tracks the window and viewport-anchored elements land where the
      // user actually sees them. Verified: a body with `max-width; margin:auto`
      // captured at the body box (600px) and dropped a fixed badge; capturing
      // <html> captured the full window (900px) and kept it.
      //
      // Bonus: capturing the root also makes html-rooted theming (Tailwind
      // `class="dark"`, inline `color-scheme`, `data-theme`) match natively in
      // the clone — those selectors are anchored on <html>, which is now the
      // clone root — so the old body-patch (mirrorRootThemingToBody) and its
      // h-full/min-height collapse work-around are no longer needed.
      const root = document.documentElement;
      // Explicit canvas dims (don't let the library measure the root).
      //
      // Height: content-sized. On `html { height: 100% }` shells (Tailwind
      // h-full, very common in Next apps) the <html> border box is one
      // viewport tall while the content overflows it — measuring the rect
      // would produce a one-viewport canvas anchored to the top of the page,
      // so a scrolled capture missed what the user was looking at. Verified
      // scrolled-to-top and scrolled-to-bottom against an h-full repro.
      //
      // Width: viewport-sized (clientWidth), NOT scrollWidth. The clone lays
      // out at the live computed width either way (so scrollWidth doesn't
      // distort layout), but we crop to the viewport horizontally regardless,
      // and hidden off-canvas elements routinely inflate scrollWidth into a
      // needlessly huge — worst case unallocatable — canvas.
      const captureW = root.clientWidth;
      const captureH = Math.max(root.scrollHeight, root.clientHeight);
      const scale = captureW > 1920 ? 1920 / captureW : 1;
      // Force-embed @font-face rules so icon fonts (Bootstrap Icons, Font
      // Awesome, Material Symbols …) don't capture as tofu squares. Best-
      // effort: never let it block capture.
      let fontCss = "";
      try {
        fontCss = await collectFontFaceCss();
      } catch (e) {
        console.warn("[feedback-hub] font collection failed, continuing", e);
      }
      // Use the page's real backdrop, not white — otherwise dark pages get a
      // white slab stamped under the foreignObject clone (Spacer Ledger
      // /activity, inbox 82ef3265). See detectRootBackground().
      const backgroundColor = detectRootBackground();
      // Crop basis: origin from the live rect (folds in the scroll offset —
      // `-top` is exactly how far the user has scrolled), dimensions from the
      // SAME captureW/H handed to the library so cropToViewport's empirical
      // scale factor resolves 1:1 against the canvas.
      const rootRect = root.getBoundingClientRect();
      const captureRect = {
        left: rootRect.left,
        top: rootRect.top,
        width: captureW,
        height: captureH,
      };
      const captureVW = root.clientWidth;
      const captureVH = root.clientHeight;
      const fullCanvas = await lib.domToCanvas(root, {
        backgroundColor,
        scale,
        width: captureW,
        height: captureH,
        timeout: 15_000,
        ...(fontCss ? { font: { cssText: fontCss } } : {}),
        filter: (node: Node) => {
          if (!(node instanceof HTMLElement)) return true;
          if (node.classList?.contains?.("fh-root")) return false;
          if (typeof node.closest === "function" && node.closest(".fh-root")) return false;
          return true;
        },
      });
      // Slice the full-page render down to the viewport the user had in
      // view when they clicked — see cropToViewport().
      const canvas = cropToViewport(fullCanvas, captureRect, captureVW, captureVH);
      console.log("[feedback-hub] capture: canvas ready", {
        full: { w: fullCanvas.width, h: fullCanvas.height },
        w: canvas.width,
        h: canvas.height,
      });

      if (timedOut) return;

      const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
          "image/png",
        );
      });
      const dataUrl = canvas.toDataURL("image/png");
      console.log("[feedback-hub] capture: blob ready", { size: blob.size });

      if (timedOut) return;
      clearTimeout(timer);
      if (fabEl) fabEl.style.display = prevDisplay;
      setEditingIndex(null);
      setPendingShot({ blob, dataUrl });
      setShotStatus("editing");
    } catch (err) {
      clearTimeout(timer);
      if (fabEl) fabEl.style.display = prevDisplay;
      if (timedOut) return;
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[feedback-hub] screenshot failed:", msg, err);
      setErrorMessage(
        msg.includes("modern-screenshot")
          ? "Couldn't load the screenshot library. Check your network and try again."
          : `Couldn't capture the screen: ${msg.slice(0, 80)}`,
      );
      setShotStatus("idle");
    }
  }

  function handleEditorDone(blob: Blob, dataUrl: string) {
    setScreenshots((prev) => {
      if (editingIndex === null) {
        // Fresh capture → append (the count cap was enforced at capture start).
        return [
          ...prev,
          { id: nextShotId(), blob, dataUrl, source: "capture" as const },
        ].slice(0, MAX_SHOTS);
      }
      // Re-annotation → replace in place, preserving the entry's source.
      const next = prev.slice();
      if (next[editingIndex]) {
        next[editingIndex] = { ...next[editingIndex], blob, dataUrl };
      }
      return next;
    });
    setPendingShot(null);
    setEditingIndex(null);
    setShotStatus("idle");
  }

  function handleEditorCancel() {
    setPendingShot(null);
    setEditingIndex(null);
    setShotStatus("idle");
  }

  // Validate, re-encode (to JPEG), and append a batch of image blobs from the
  // upload or paste sources. Capture has its own PNG path and skips this.
  async function addImageBlobs(blobs: Blob[], source: ScreenshotSource) {
    setAttachNote("");
    const accepted: ShotEntry[] = [];
    let remaining = MAX_SHOTS - screenshots.length;
    for (const b of blobs) {
      if (remaining <= 0) {
        setAttachNote(`You can attach up to ${MAX_SHOTS} images.`);
        break;
      }
      if (!b.type.startsWith("image/")) {
        setAttachNote("Only image files can be attached.");
        continue;
      }
      if (b.size > MAX_SRC_BYTES) {
        setAttachNote("Each image must be under 10 MB.");
        continue;
      }
      try {
        const { blob, dataUrl } = await normalizeImageToJpeg(b);
        accepted.push({ id: nextShotId(), blob, dataUrl, source });
        remaining--;
      } catch (err) {
        console.error("[feedback-hub] image normalize failed", err);
        setAttachNote("Couldn't read that image. Try a different file.");
      }
    }
    if (accepted.length > 0) {
      setScreenshots((prev) => [...prev, ...accepted].slice(0, MAX_SHOTS));
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) void addImageBlobs(files, "upload");
    // Reset so selecting the same file again still fires onChange.
    e.target.value = "";
  }

  // Always-on paste path. Works in INSECURE contexts (plain http on a LAN IP),
  // where navigator.clipboard.read is unavailable — the ClipboardEvent still
  // carries pasted image blobs. preventDefault only when we actually consume an
  // image, so pasting text into the textarea is unaffected.
  function handlePanelPaste(e: React.ClipboardEvent) {
    const items = e.clipboardData?.items;
    if (!items) return;
    const blobs: Blob[] = [];
    for (const it of Array.from(items)) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) blobs.push(f);
      }
    }
    if (blobs.length > 0) {
      e.preventDefault();
      void addImageBlobs(blobs, "paste");
    }
  }

  // Progressive enhancement: the async Clipboard API only exists in a secure
  // context. When it's there, read images directly; otherwise nudge the user to
  // press ⌘/Ctrl+V (handled by handlePanelPaste), which is the always-on path.
  async function handlePasteButton() {
    setAttachNote("");
    if (screenshots.length >= MAX_SHOTS) {
      setAttachNote(`You can attach up to ${MAX_SHOTS} images.`);
      return;
    }
    const clip = navigator.clipboard as
      | { read?: () => Promise<ClipboardItem[]> }
      | undefined;
    if (window.isSecureContext && typeof clip?.read === "function") {
      try {
        const items = await clip.read();
        const blobs: Blob[] = [];
        for (const it of items) {
          const type = it.types.find((t) => t.startsWith("image/"));
          if (type) blobs.push(await it.getType(type));
        }
        if (blobs.length > 0) {
          await addImageBlobs(blobs, "paste");
          return;
        }
        setAttachNote("No image on the clipboard — copy one, then paste.");
        return;
      } catch {
        // Permission denied / not focused — fall through to the manual hint.
      }
    }
    setAttachNote("Press ⌘/Ctrl+V to paste an image here.");
    textareaRef.current?.focus();
  }

  function removeShot(id: string) {
    setScreenshots((prev) => prev.filter((s) => s.id !== id));
  }

  function editShot(index: number) {
    const s = screenshots[index];
    if (!s) return;
    setEditingIndex(index);
    setPendingShot({ blob: s.blob, dataUrl: s.dataUrl });
    setShotStatus("editing");
  }

  async function handleSubmit(e?: React.FormEvent | React.KeyboardEvent) {
    if (e) e.preventDefault();
    if (!canSubmit) return;
    const t0 = performance.now();
    setStatus("sending");
    setErrorMessage("");

    try {
      // Upload every attached image in parallel; each gets its own signed URL.
      // Order is preserved so it maps 1:1 onto the source list for provenance.
      let screenshotPayload: FeedbackScreenshot[] | undefined;
      if (screenshots.length > 0) {
        const paths = await Promise.all(
          screenshots.map((s) => uploadScreenshot(endpoint, apiKey, s.blob)),
        );
        screenshotPayload = paths.map((path, i) => ({
          path,
          source: screenshots[i].source,
        }));
      }

      const consoleLog = getConsoleBuffer();
      const payload: FeedbackPayload = {
        type,
        body: body.trim(),
        url: window.location.href,
        viewport: {
          w: window.innerWidth,
          h: window.innerHeight,
          dpr: window.devicePixelRatio || 1,
        },
        user_agent: navigator.userAgent,
        reporter_user_id: user?.id,
        reporter_email: user?.email,
        screenshots: screenshotPayload,
        console_log: consoleLog.length > 0 ? consoleLog : undefined,
      };

      const res = await sendFeedback(endpoint, apiKey, payload);
      const ms = Math.round(performance.now() - t0);
      setDuration(ms);
      setViewUrl(res.view_url ?? null);
      setLinkCopied(false);
      setStatus("sent");
      setBody("");
      setScreenshots([]);
      setAttachNote("");
      // Auto-close longer when we have a tracking link to give the user
      // time to notice + click/copy it. Plain success: short bounce.
      setTimeout(() => setOpen(false), res.view_url ? 6000 : 1700);
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        err instanceof Error && err.message.includes("upload")
          ? "Couldn't upload the screenshot. Try again, or send without it."
          : "Couldn't reach the server. Try again?",
      );
    }
  }

  function handleTextareaKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // ⌘/Ctrl + Enter submits
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e);
    }
  }

  const rootStyle = {
    "--fh-accent": accent,
    ...(offset?.bottom !== undefined && { "--fh-bottom": `${offset.bottom}px` }),
    ...(offset?.side !== undefined && { "--fh-side": `${offset.side}px` }),
    ...(offset?.bottomMobile !== undefined && {
      "--fh-bottom-mobile": `${offset.bottomMobile}px`,
    }),
  } as React.CSSProperties;

  return (
    <div
      className={`fh-root ${positionClass}${open ? " fh-open" : ""}${firstVisit ? " fh-first-visit" : ""}`}
      data-mobile-bottom={offset?.bottomMobile !== undefined ? "" : undefined}
      style={rootStyle}
    >
      {shotStatus === "capturing" && (
        <div className="fh-editor" aria-label="Capturing screenshot" role="status">
          <div className="fh-editor-toolbar">
            <span className="fh-editor-tools" style={{ background: "transparent", padding: 0 }}>
              <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase" }}>
                Capturing…
              </span>
            </span>
          </div>
          <div className="fh-editor-stage">
            <div className="fh-capture-loader">
              <div className="fh-capture-spin" aria-hidden />
              <div className="fh-capture-label">Drawing the page</div>
              <div className="fh-capture-sub">
                Hold on while we snapshot what you&rsquo;re looking at.
              </div>
            </div>
          </div>
        </div>
      )}

      {shotStatus === "editing" && pendingShot && (
        <ScreenshotEditor
          imageDataUrl={pendingShot.dataUrl}
          accent={accent}
          onCancel={handleEditorCancel}
          onDone={handleEditorDone}
        />
      )}
      {open && (
        <div
          ref={panelRef}
          className="fh-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Send feedback"
          onPaste={handlePanelPaste}
        >
          {status === "sent" ? (
            <div className="fh-success" role="status">
              <span className="fh-success-mark" aria-hidden>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12.5l4 4 10-10"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div className="fh-success-title">Thanks — we got it.</div>
              <div className="fh-success-sub">
                {viewUrl
                  ? "Save this link to check the status later:"
                  : "You can keep using the app."}
              </div>
              {viewUrl && (
                <div className="fh-success-track">
                  <a
                    className="fh-success-track-link"
                    href={viewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {viewUrl.replace(/^https?:\/\//, "")}
                  </a>
                  <button
                    type="button"
                    className="fh-success-track-copy"
                    onClick={async () => {
                      const ok = await copyToClipboard(viewUrl);
                      if (ok) {
                        setLinkCopied(true);
                        setTimeout(() => setLinkCopied(false), 1800);
                      }
                      // If even the fallback failed, the link above is
                      // still clickable / long-press-selectable.
                    }}
                    aria-label="Copy tracking link"
                  >
                    {linkCopied ? "Copied" : "Copy"}
                  </button>
                </div>
              )}
              {duration !== null && (
                <div className="fh-success-stat">
                  sent · {duration}ms
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="fh-header">
                <span className="fh-title">
                  <span className="fh-title-dot" aria-hidden />
                  Send feedback
                </span>
                <button
                  type="button"
                  className="fh-close"
                  aria-label="Close"
                  onClick={() => {
                    setOpen(false);
                    fabRef.current?.focus();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 6 L18 18 M18 6 L6 18"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>

              <div className="fh-types" role="group" aria-label="Feedback type">
                {TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    className="fh-type"
                    aria-pressed={type === t.value}
                    onClick={() => setType(t.value)}
                    style={
                      {
                        ["--fh-chip-color" as string]: t.color,
                        ["--fh-chip-tint" as string]: t.tint,
                      } as React.CSSProperties
                    }
                  >
                    <span className="fh-type-glyph" aria-hidden>
                      {t.glyph}
                    </span>
                    {t.label}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit}>
                <div className="fh-body">
                  <textarea
                    ref={textareaRef}
                    className="fh-textarea"
                    placeholder={PLACEHOLDERS[type]}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={handleTextareaKey}
                    disabled={status === "sending"}
                    style={
                      {
                        ["--fh-accent" as string]: activeChip.color,
                      } as React.CSSProperties
                    }
                  />
                </div>

                <div className="fh-attach">
                  {screenshots.length > 0 && (
                    <div className="fh-shots">
                      {screenshots.map((s, i) => (
                        <div key={s.id} className="fh-shot-item">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={s.dataUrl}
                            alt={`Screenshot ${i + 1}`}
                            className="fh-shot-thumb"
                          />
                          <span className="fh-shot-src">{s.source}</span>
                          <div className="fh-shot-actions">
                            <button
                              type="button"
                              className="fh-shot-act"
                              aria-label={`Annotate screenshot ${i + 1}`}
                              title="Annotate"
                              onClick={() => editShot(i)}
                              disabled={status === "sending"}
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M4 20h4L18 10l-4-4L4 16v4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                <path d="M13.5 6.5l4 4" stroke="currentColor" strokeWidth="1.8" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="fh-shot-act"
                              aria-label={`Remove screenshot ${i + 1}`}
                              title="Remove"
                              onClick={() => removeShot(s.id)}
                              disabled={status === "sending"}
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {screenshots.length < MAX_SHOTS && (
                    <div className="fh-add-row" role="group" aria-label="Add a screenshot">
                      <button
                        type="button"
                        className="fh-add-btn"
                        onClick={handleCaptureScreenshot}
                        disabled={shotStatus === "capturing" || status === "sending"}
                      >
                        {shotStatus === "capturing" ? (
                          <>
                            <span className="fh-submit-spin" aria-hidden />
                            Capturing…
                          </>
                        ) : (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <rect x="3" y="6" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
                              <path d="M8 6l2-3h4l2 3" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                              <circle cx="12" cy="13" r="3.5" stroke="currentColor" strokeWidth="1.7" />
                            </svg>
                            Capture
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="fh-add-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={status === "sending"}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path d="M12 15V3 M8 7l4-4 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                        Upload
                      </button>
                      <button
                        type="button"
                        className="fh-add-btn"
                        onClick={handlePasteButton}
                        onMouseEnter={() => setShowPasteHint(true)}
                        onMouseLeave={() => setShowPasteHint(false)}
                        onFocus={() => setShowPasteHint(true)}
                        onBlur={() => setShowPasteHint(false)}
                        disabled={status === "sending"}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <rect x="5" y="5" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.7" />
                          <path d="M9 5a3 3 0 0 1 6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                          <path d="M9 3.5h6v3H9z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                        </svg>
                        Paste
                      </button>
                    </div>
                  )}

                  {attachNote ? (
                    <div className="fh-attach-note" role="status">
                      {attachNote}
                    </div>
                  ) : showPasteHint ? (
                    <div className="fh-attach-note">
                      Copy an image, then press ⌘/Ctrl+V to paste it here.
                    </div>
                  ) : null}
                </div>

                {provenance && (
                  <div className="fh-prov" aria-label="What will be attached">
                    <svg
                      className="fh-prov-icon"
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden
                    >
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M3 12h18 M12 3a14 14 0 0 1 0 18 M12 3a14 14 0 0 0 0 18" stroke="currentColor" strokeWidth="1.6" />
                    </svg>
                    <span className="fh-prov-host">{provenance.host}</span>
                    <span className="fh-prov-sep">·</span>
                    <span className="fh-prov-vp">{provenance.vp}</span>
                  </div>
                )}

                {status === "error" && (
                  <div className="fh-error" role="alert">
                    <span className="fh-error-dot" aria-hidden />
                    {errorMessage}
                  </div>
                )}

                <div className="fh-footer">
                  <span
                    className="fh-count"
                    data-over={overLimit}
                    data-active={charCount > 0}
                  >
                    {charCount.toLocaleString()} / {MAX_LEN.toLocaleString()}
                  </span>
                  <div className="fh-submit-wrap">
                    {canSubmit && (
                      <span className="fh-kbd-hint" aria-hidden>
                        <kbd>⌘</kbd>
                        <kbd>↵</kbd>
                      </span>
                    )}
                    <button
                      type="submit"
                      className="fh-submit"
                      disabled={!canSubmit}
                      aria-busy={status === "sending"}
                    >
                      {status === "sending" ? (
                        <>
                          <span className="fh-submit-spin" aria-hidden />
                          Sending
                        </>
                      ) : (
                        <>
                          Send
                          <svg
                            className="fh-submit-arrow"
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden
                          >
                            <path
                              d="M5 12h14 M13 6l6 6-6 6"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="fh-foot">
                  <span>
                    feedback/hub
                    <span className="fh-foot-ver" title={WIDGET_VERSION_LABEL}>
                      v{WIDGET_VERSION}
                    </span>
                  </span>
                  <span>esc to close</span>
                </div>
              </form>
            </>
          )}
        </div>
      )}

      {/* Outside the panel so it stays clear of the dialog focus trap; opened
          programmatically via the Upload button. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        aria-hidden
        tabIndex={-1}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <button
        ref={fabRef}
        type="button"
        className="fh-fab"
        aria-label={label ?? "Send feedback"}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M4 5h13a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H9l-5 4z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          <circle cx="10" cy="11" r="0.9" fill="currentColor" />
          <circle cx="14" cy="11" r="0.9" fill="currentColor" />
        </svg>
        {label}
        <svg
          className="fh-fab-chevron"
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
        >
          <path
            d="M9 6 l6 6 -6 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}
